import {
  PLATFORM_ROLE_LABELS,
  PLATFORM_ROLES,
  type PlatformRole,
  type PlatformStaff,
} from "@station/contracts";
import {
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Select,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Users } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddStaffDialog } from "../../features/platform/ops-dialogs.js";
import {
  usePlatformMe,
  usePlatformPermissions,
  usePlatformStaff,
  useRefreshPlatform,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { formatDateTime } from "../../lib/format.js";
import { useStepUp } from "../../lib/step-up.js";

export const Route = createFileRoute("/platform/personel")({ component: StaffRoute });

/**
 * Platform personeli (Prompt 24 web): süper yönetici / destek / finans / salt okunur. Rol yalnızca var olan bir kullanıcı
 * hesabına verilir; değiştirme ve kaldırma step-up ister, son süper yönetici kaldırılamaz (sunucu korur).
 */
function StaffRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const withStepUp = useStepUp();
  const permissions = usePlatformPermissions();
  const me = usePlatformMe();
  const canManage = permissions.has("platform.staff.manage");
  const staff = usePlatformStaff();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PlatformStaff | null>(null);
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: PlatformRole | null }) =>
      withStepUp(
        async () =>
          (await api.PUT("/platform/staff/{userId}/role", { params: { path: { userId } }, body: { role } }))
            .data!,
      ),
    onSuccess: async (_result, variables) => {
      await refresh();
      toast({
        tone: "success",
        title: t(variables.role ? "platform.staff.roleChanged" : "platform.staff.removed"),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  const selfId = me.data?.user.id;

  const columns = useMemo<DataTableColumn<PlatformStaff>[]>(
    () => [
      {
        id: "name",
        header: t("platform.fields.name"),
        cell: (row) => (
          <span>
            <span className="font-medium">{row.fullName}</span>
            <span className="block text-xs text-muted-foreground">{row.email}</span>
          </span>
        ),
      },
      {
        id: "role",
        header: t("platform.fields.role"),
        cell: (row) =>
          canManage && row.id !== selfId ? (
            <Select
              aria-label={t("platform.staff.roleFor", { name: row.fullName })}
              value={row.role}
              disabled={setRole.isPending}
              onChange={(event) =>
                setRole.mutate({ userId: row.id, role: event.target.value as PlatformRole })
              }
            >
              {PLATFORM_ROLES.map((value) => (
                <option key={value} value={value}>
                  {PLATFORM_ROLE_LABELS[value]}
                </option>
              ))}
            </Select>
          ) : (
            PLATFORM_ROLE_LABELS[row.role]
          ),
      },
      {
        id: "mfa",
        header: t("platform.fields.mfa"),
        cell: (row) =>
          row.mfaEnabled ? (
            <StatusBadge tone="success">{t("platform.staff.mfaOn")}</StatusBadge>
          ) : (
            <StatusBadge tone="warning">{t("platform.staff.mfaOff")}</StatusBadge>
          ),
      },
      {
        id: "login",
        header: t("platform.fields.lastLogin"),
        cell: (row) => (row.lastLoginAt ? formatDateTime(row.lastLoginAt) : "—"),
      },
      {
        id: "actions",
        header: t("platform.fields.actions"),
        cell: (row) =>
          canManage && row.id !== selfId ? (
            <Button size="sm" variant="ghost" onClick={() => setRemoving(row)}>
              {t("platform.staff.remove")}
            </Button>
          ) : null,
      },
    ],
    [t, canManage, selfId, setRole],
  );

  return (
    <PlatformPage
      title={t("platform.staff.title")}
      description={t("platform.staff.description")}
      permission="platform.catalog.read"
      actions={
        canManage ? (
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t("platform.staff.add")}
          </Button>
        ) : null
      }
    >
      <DataTable
        caption={t("platform.staff.title")}
        columns={columns}
        rows={staff.data ?? []}
        rowKey={(row) => row.id}
        loading={staff.isPending}
        error={staff.error ? errorMessage(staff.error) : undefined}
        emptyState={<EmptyState icon={<Users />} title={t("platform.staff.empty")} />}
      />
      {adding ? <AddStaffDialog open onOpenChange={setAdding} /> : null}
      {removing ? (
        <ConfirmDialog
          open
          tone="danger"
          onOpenChange={(open) => !open && setRemoving(null)}
          title={t("platform.staff.removeTitle", { name: removing.fullName })}
          description={t("platform.staff.removeDescription")}
          confirmLabel={t("platform.staff.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={() =>
            setRole.mutateAsync({ userId: removing.id, role: null }).then(
              () => undefined,
              () => undefined,
            )
          }
        />
      ) : null}
    </PlatformPage>
  );
}
